import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import LangInput from "@/components/LangInput";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import {
  ArrowLeft, Plus, MapPin, Sparkle, Bug, CloudRain, Sun, Leaf, Warning,
  PaperPlaneTilt, Image as ImageIcon, X, Check,
} from "@phosphor-icons/react";

/**
 * FarmersReport — /farmers/report
 * The "+ REPORT WHAT YOU SEE" form (section 9). Reuses NaturePulse's
 * LangInput (multilingual text + voice) and photo uploads. When the
 * user submits, the backend writes the report to both `farmer_reports`
 * AND `observations_v2` (with a `farmers-pulse` context tag) so
 * every NaturePulse surface — feed, map, species pages — surfaces the
 * report automatically.
 */

const CATEGORY_ICONS = {
  crop: Leaf, plant: Leaf, tree: Leaf, flowering: Sparkle, fruiting: Sparkle,
  harvest: Leaf, pest: Bug, disease: Bug, insect: Bug, pollinator: Sparkle,
  bird: Sparkle, animal: Sparkle, rain: CloudRain, flood: CloudRain,
  drought: Sun, frost: Sun, heat: Sun, soil: Leaf, water: CloudRain,
  storm: CloudRain, damage: Warning, other: Sparkle,
};

const SEVERITY_META = {
  none: { label: "None", tone: "bg-white border-black/15 text-[#3A3830]" },
  low: { label: "Low", tone: "bg-amber-50 border-amber-200 text-amber-800" },
  medium: { label: "Medium", tone: "bg-orange-50 border-orange-200 text-orange-800" },
  high: { label: "High", tone: "bg-rose-50 border-rose-200 text-rose-800" },
  critical: { label: "Critical", tone: "bg-rose-100 border-rose-300 text-rose-900" },
};

export default function FarmersReport() {
  const { user, loading, requireSignIn } = useAuth();
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [form, setForm] = useState({
    category: "", title: "", description: "", crop: "", species: "",
    severity: "", latitude: "", longitude: "",
    country: "", state: "", district: "", village: "",
    tags: "", media: [],
    share_with_naturepulse: true, privacy: "public",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [farms, setFarms] = useState([]);

  useEffect(() => {
    http.get("/farmers/observations/categories")
      .then(({ data }) => { setCategories(data.categories || []); setSeverities(data.severities || []); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    http.get("/farmers/farms/mine").then(({ data }) => setFarms(data.farms || [])).catch(() => {});
  }, [user, loading]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation is not supported here.");
    navigator.geolocation.getCurrentPosition((p) => {
      setForm((f) => ({ ...f, latitude: String(p.coords.latitude.toFixed(6)), longitude: String(p.coords.longitude.toFixed(6)) }));
      toast.success("Location captured");
    }, () => toast.error("Could not read your location."));
  };

  const handlePhotos = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files).slice(0, 6)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await http.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.url) uploaded.push({ url: data.url, media_type: "image" });
      }
      setForm((f) => ({ ...f, media: [...f.media, ...uploaded].slice(0, 6) }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not upload photo"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!user) return requireSignIn("submit a farmer report");
    if (!form.category) return toast.error("Pick a category first.");
    setSaving(true);
    try {
      const payload = {
        category: form.category,
        title: form.title.trim() || undefined,
        description: form.description.trim() || undefined,
        crop: form.crop.trim() || undefined,
        species: form.species.trim() || undefined,
        severity: form.severity || undefined,
        latitude: form.latitude ? parseFloat(form.latitude) : undefined,
        longitude: form.longitude ? parseFloat(form.longitude) : undefined,
        country: form.country.trim() || undefined,
        state: form.state.trim() || undefined,
        district: form.district.trim() || undefined,
        village: form.village.trim() || undefined,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 12),
        media: form.media,
        share_with_naturepulse: form.share_with_naturepulse,
        privacy: form.privacy,
      };
      const { data } = await http.post("/farmers/observations", payload);
      toast.success("Report submitted — thank you!");
      // Non-admin reports enter moderation queue; go to Farmers home rather
      // than /observations/{id} which 404s for pending docs.
      nav("/farmers?submitted=1", { replace: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit report"); }
    finally { setSaving(false); }
  };

  const heroText = "Farmers Pulse — Report what you see. A single photo and a sentence can help thousands of farmers spot the next pest outbreak, unusual bloom, or shifting rain pattern.";

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="farmers-report-page">
      <div className="max-w-[820px] mx-auto">
        <Link to="/farmers" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <div className="mt-3 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
          <Plus size={12} weight="bold" /> / Report what you see
        </div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
          What is happening on your farm today?
        </h1>
        <p className="mt-3 text-sm md:text-base text-[#3A3830] max-w-2xl">
          A single observation may seem small — thousands together can spot the next pest outbreak, unusual bloom, or shifting rain pattern.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <ListenTranslateBar text={heroText} testidPrefix="farmers-report-tools" />
          <ShareButton title="Report what you see — Farmers Pulse" text={heroText} variant="chip" testid="farmers-report-share" />
        </div>

        {/* Category picker */}
        <div className="mt-6" data-testid="farmers-report-category">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Category *</div>
          <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {categories.map((c) => {
              const Icon = CATEGORY_ICONS[c] || Sparkle;
              const active = form.category === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, category: active ? "" : c })}
                  data-testid={`category-${c}`}
                  className={`rounded-lg border p-3 flex flex-col items-center gap-1 text-xs capitalize transition ${
                    active
                      ? "bg-[#241209] text-[#F5EBDC] border-[#241209]"
                      : "bg-white border-black/15 text-[#241209] hover:border-black/40"
                  }`}
                >
                  <Icon size={16} weight="fill" />
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        {/* Form fields */}
        <div className="mt-6 rounded-2xl border border-black/10 bg-white p-5 md:p-6 space-y-4" data-testid="farmers-report-form">
          <Field label="Title (optional)">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="report-input-title" placeholder="e.g. Fall armyworm damage on maize" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="What did you see?">
            <LangInput as="textarea" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} testid="report-input-description" placeholder="A few sentences describing what you saw — use your keyboard or the mic to dictate in any language." />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Crop (optional)"><input value={form.crop} onChange={(e) => setForm({ ...form, crop: e.target.value })} data-testid="report-input-crop" placeholder="e.g. Maize" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Species (optional)"><input value={form.species} onChange={(e) => setForm({ ...form, species: e.target.value })} data-testid="report-input-species" placeholder="Scientific or common name" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
          </div>

          {/* Severity */}
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Severity (optional)</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {severities.map((s) => {
                const meta = SEVERITY_META[s] || { label: s, tone: "bg-white border-black/15" };
                const active = form.severity === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm({ ...form, severity: active ? "" : s })}
                    data-testid={`severity-${s}`}
                    className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase ${
                      active ? "bg-[#241209] text-[#F5EBDC] border-[#241209]" : meta.tone
                    }`}
                  >{meta.label}</button>
                );
              })}
            </div>
          </div>

          {/* Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Village / town"><input value={form.village} onChange={(e) => setForm({ ...form, village: e.target.value })} data-testid="report-input-village" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="District / state / country">
              <div className="flex gap-2">
                <input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="report-input-district" placeholder="District" className="flex-1 bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
                <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="report-input-state" placeholder="State" className="w-24 bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
                <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="report-input-country" placeholder="Country" className="w-24 bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
              </div>
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Latitude"><input value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} data-testid="report-input-lat" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Longitude"><input value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} data-testid="report-input-lon" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <div className="grid gap-2">
              <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Auto</label>
              <button type="button" onClick={useMyLocation} data-testid="report-use-location" className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm hover:bg-black/5 inline-flex items-center gap-1.5">
                <MapPin size={12} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> Capture GPS
              </button>
            </div>
          </div>

          {/* Media */}
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Photos (up to 6)</div>
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              {form.media.map((m, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-black/10">
                  <img src={m.url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, media: f.media.filter((_, j) => j !== i) }))}
                    data-testid={`report-media-remove-${i}`}
                    className="absolute top-0.5 right-0.5 rounded-full bg-black/70 text-white p-0.5 hover:bg-black"
                  ><X size={10} weight="bold" /></button>
                </div>
              ))}
              <label className="w-20 h-20 rounded-lg border border-dashed border-black/20 grid place-items-center text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830] hover:bg-white cursor-pointer" data-testid="report-media-add">
                {uploading ? "…" : <><ImageIcon size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)] mb-1" />Add</>}
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handlePhotos(e.target.files)} />
              </label>
            </div>
          </div>

          <Field label="Tags (comma-separated)">
            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="report-input-tags" placeholder="e.g. kerala, monsoon, coconut" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
          </Field>

          {/* Sharing controls */}
          <div className="pt-2 rounded-lg border border-black/10 bg-[#FBF2E1]/50 p-4">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Sharing</div>
            <label className="mt-2 flex items-start gap-2 text-sm">
              <input type="checkbox" checked={form.share_with_naturepulse} onChange={(e) => setForm({ ...form, share_with_naturepulse: e.target.checked })} data-testid="report-share-naturepulse" className="mt-0.5" />
              <span>Share to NaturePulse feed, map and species pages. Uncheck to keep this report inside Farmers Pulse.</span>
            </label>
            <div className="mt-3">
              <Field label="Privacy">
                <select value={form.privacy} onChange={(e) => setForm({ ...form, privacy: e.target.value })} data-testid="report-input-privacy" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm">
                  <option value="public">Public — anyone can view</option>
                  <option value="community">Community — nearby farmers</option>
                  <option value="private">Private — only me</option>
                </select>
              </Field>
            </div>
          </div>

          <div className="pt-2 flex justify-end gap-2">
            <button onClick={() => nav("/farmers")} className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">Cancel</button>
            <button onClick={submit} disabled={saving || !form.category} data-testid="report-submit" className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
              <Check size={12} weight="bold" /> {saving ? "Submitting…" : "Submit report"}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}


const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
